import tinycolor from "tinycolor2";
import { round } from "./round";
import {
  RgbaColor,
  RgbColor,
  HslaColor,
  HslColor,
  HsvaColor,
  HsvColor,
} from "../types";

// Keep core conversion functions that are used by color picker components
export const hexToHsva = (hex: string): HsvaColor => {
  const color = tinycolor(hex);
  const hsv = color.toHsv();
  return {
    h: round(hsv.h || 0),
    s: round(hsv.s * 100),
    v: round(hsv.v * 100),
    a: round(hsv.a, 2),
  };
};

export const hexToRgba = (hex: string): RgbaColor => {
  const color = tinycolor(hex);
  const rgb = color.toRgb();
  return {
    r: rgb.r,
    g: rgb.g,
    b: rgb.b,
    a: round(rgb.a, 2),
  };
};

export const hsvaToRgba = ({ h, s, v, a }: HsvaColor): RgbaColor => {
  const color = tinycolor({ h, s: s / 100, v: v / 100, a });
  const rgb = color.toRgb();
  return {
    r: rgb.r,
    g: rgb.g,
    b: rgb.b,
    a: round(rgb.a, 2),
  };
};

export const rgbaToHsva = ({ r, g, b, a }: RgbaColor): HsvaColor => {
  const color = tinycolor({ r, g, b, a });
  const hsv = color.toHsv();
  return {
    h: round(hsv.h || 0),
    s: round(hsv.s * 100),
    v: round(hsv.v * 100),
    a: round(hsv.a, 2),
  };
};

export const rgbaToHex = ({ r, g, b, a }: RgbaColor): string => {
  const color = tinycolor({ r, g, b, a });
  return color.toHexString();
};

export const hsvaToHex = (hsva: HsvaColor): string => {
  const color = tinycolor({
    h: hsva.h,
    s: hsva.s / 100,
    v: hsva.v / 100,
    a: hsva.a,
  });
  return color.toHexString();
};

export const roundHsva = (hsva: HsvaColor): HsvaColor => ({
  h: round(hsva.h),
  s: round(hsva.s),
  v: round(hsva.v),
  a: round(hsva.a, 2),
});

// Keep utility functions for backward compatibility
export const rgbaToRgb = ({ r, g, b }: RgbaColor): RgbColor => ({ r, g, b });
export const hslaToHsl = ({ h, s, l }: HslaColor): HslColor => ({ h, s, l });
export const hsvaToHsv = (hsva: HsvaColor): HsvColor => {
  const { h, s, v } = roundHsva(hsva);
  return { h, s, v };
};

// String conversion functions needed by color picker components
export const hsvaToHslString = (hsva: HsvaColor): string => {
  const color = tinycolor({
    h: hsva.h,
    s: hsva.s / 100,
    v: hsva.v / 100,
    a: hsva.a,
  });
  return color.toHslString();
};

export const hsvaToHslaString = (hsva: HsvaColor): string => {
  const color = tinycolor({
    h: hsva.h,
    s: hsva.s / 100,
    v: hsva.v / 100,
    a: hsva.a,
  });
  return color.toHslString();
};
